<template>
    <div>
        <limitless-page-header :loading="busy_loading" :title="'Dokument'" :sub-title="document_name" :title-id="document_id">
            <template #header-elements>
                <limitless-page-header-button url="/documents" icon="icon-circle-left2">Tillbaka</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div class="content">
            <tb-skeleton v-if="busy_loading" :height="100" :theme="'opacity'" :shape="'radius'" bg-color="#dcdbdc"></tb-skeleton>

            <div v-else-if="!busy_loading && document">
                <div class="row">
                    <div class="col-lg-6">
                        <div class="card">
                            <div class="card-body">
                                <ValidationObserver ref="documentform">
                                    <form @submit.prevent="saveDocument">
                                        <div class="row">
                                            <label class="col-form-label col-lg-2">Namn: <span class="text-danger">*</span></label>
                                            <div class="col-lg-10">
                                                <input-with-validation
                                                    vid="name"
                                                    label="Namn"
                                                    rules="required"
                                                    v-model="document.name"
                                                    hide-label
                                                />
                                            </div>
                                        </div>
                                        <div v-if="document.uuid !== ''" class="row">
                                            <label class="col-form-label col-lg-2">Ursprungligt filnamn:</label>
                                            <div class="col-lg-10">
                                                <div class="form-group">
                                                    <input type="text" class="form-control" v-model="document.original_name" readonly>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <label class="col-form-label col-lg-2">Kategori: <span class="text-danger">*</span></label>
                                            <div class="col-lg-10">
                                                <select-with-validation
                                                    vid="document_category_id"
                                                    label="Kategori"
                                                    rules="required"
                                                    v-model="document.document_category_id"
                                                    :options="categories"
                                                    :reduce="item => item.id"
                                                    option-label="name"
                                                    placeholder="Välj..."
                                                    hide-label
                                                />
                                            </div>
                                        </div>
                                        <div class="row">
                                            <label class="col-form-label col-lg-2">{{ (document.uuid === '' ? 'Fil' : 'Ladda upp ny version') }}: <span v-if="document.uuid === ''" class="text-danger">*</span></label>
                                            <div class="col-lg-10">
                                                <ValidationProvider vid="file" name="Fil" rules="" v-slot="{ errors }">
                                                    <div class="form-group">
                                                        <single-file-uploader ref="fileInput" :url="document.uuid ? `/vue/documents/${document.uuid}/replace` : '/vue/documents/upload'"
                                                            @input="selected_file = $event" :disabled="busy_saving"
                                                            accept=".jpg,.jpeg,.png,.pdf,.csv,.xml,.pps,.ppt,.ppsx,.pptx,.odp,.xls,.xlsx,.ods,.txt,.doc,.docx,.odt,.rtf,.zip"
                                                            extensions="jpg,jpeg,png,pdf,csv,xml,pps,ppt,ppsx,pptx,odp,xls,xlsx,ods,txt,doc,docx,odt,rtf,zip" />
                                                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </div>
                                                </ValidationProvider>
                                            </div>
                                        </div>
                                    </form>
                                </ValidationObserver>
                            </div>
                            <div class="card-footer">
                                <div class="d-flex justify-content-end">
                                    <limitless-button v-if="document.uuid !== ''" ref="btndeletedocument" type="button" @click="deleteDocument" :busy="busy_saving" icon="icon-cross2" btn-class="btn-danger" class="mr-auto">RADERA</limitless-button>
                                    <limitless-button ref="btnsavedocument" type="button" @click="saveDocument" :busy="busy_saving" icon="icon-floppy-disk">SPARA</limitless-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div> <!-- /content -->
    </div>
</template>

<script>
import Vue from 'vue';
import apiClient from '@services/ApiClient';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import SingleFileUploader from '@components/SingleFileUploader';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import getError from '@services/ErrorHelper';
import InputWithValidation from '@components/Form/InputWithValidation.vue';
import SelectWithValidation from '@components/Form/SelectWithValidation.vue';

export default {
    name: 'DocumentEdit',
    components: {
        SelectWithValidation,
        InputWithValidation,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
        SingleFileUploader,
    },
    props: {
        uuid: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        busy_loading: true,
        busy_saving: false,
        categories: [],
        document: {
            uuid: '',
            name: '',
            document_category_id: '',
        },
        selected_file: null,
    }),
    mounted() {
        this.getCategories();
        this.init();
    },
    watch: {
        id() {
            this.init();
        },
    },
    methods: {
        init() {
            this.getDocument();
        },
        getDocument() {
            const vm = this;
            if (this.uuid) {
                vm.busy_loading = true;
                apiClient.get('/vue/documents/' + this.uuid + '')
                    .then((response) => {
                        vm.document = response.data.document;
                    })
                    .finally(() => {
                        vm.busy_loading = false;
                    });
            } else {
                vm.busy_loading = false;
            }
        },
        async saveDocument() {
            if (this.busy_saving) return;
            const isValid = await this.$refs.documentform.validate();
            if (!isValid) return;
            if (!this.document.uuid && !this.selected_file) {
                this.$refs.documentform.setErrors({ file: ['Välj en fil först.'] });
                return;
            }
            this.busy_saving = true;
            try {
                if (this.document.uuid && this.selected_file) {
                    const result = await this.$swal({
                        title: 'Ersätt dokumentfilen?',
                        text: 'Den nya filen används överallt där dokumentet används.',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Ja, ersätt filen',
                        cancelButtonText: 'Avbryt',
                    });
                    if (!result.isConfirmed) return;
                }
                if (this.selected_file) this.$refs.fileInput.validateSelection();
                if (!this.document.uuid) {
                    const uploaded = await this.$refs.fileInput.upload({
                        document_name: this.document.name,
                        document_category_id: this.document.document_category_id,
                    });
                    this.document = uploaded.file;
                    this.$refs.fileInput.clear();
                } else {
                    const response = await apiClient.post('/vue/documents/' + this.document.uuid, {
                        name: this.document.name,
                        document_category_id: this.document.document_category_id,
                    });
                    this.document = response.data.document;
                    if (this.selected_file) {
                        const uploaded = await this.$refs.fileInput.upload({ confirmed: '1' });
                        this.document = uploaded.file;
                        this.$refs.fileInput.clear();
                    }
                }
                if (this.uuid !== this.document.uuid) {
                    window.location.href = '/documents/' + this.document.uuid;
                }
                this.$refs.btnsavedocument.triggerSaved();
            } catch (error) {
                const parsederror = getError(error);
                if (error.response && typeof parsederror === 'object') {
                    this.$refs.documentform.setErrors(parsederror);
                } else {
                    this.$refs.documentform.setErrors({ file: [error.message || 'Filen kunde inte laddas upp.'] });
                }
                this.$refs.btnsavedocument.triggerFailed();
            } finally {
                this.busy_saving = false;
            }
        },
        deleteDocument() {
            if (this.busy_saving) return;
            const vm = this;
            // eslint-disable-next-line no-restricted-globals,no-alert
            if (confirm('Are you sure you want to delete this document?')) {
                vm.busy_saving = true;
                apiClient.delete('/vue/documents/' + this.uuid)
                    .then(() => {
                        document.location.href = '/documents';
                        Vue.set(vm, 'busy_saving', false);
                    })
                    .catch(() => {
                        vm.$refs.btndeletedocument.triggerFailed();
                    })
                    .finally(() => {
                        vm.busy_saving = false;
                    });
            }
        },
        getCategories() {
            apiClient.get('/vue/documents/categories/table').then((res) => {
                this.categories = res.data.data;
            });
        },
    },
    computed: {
        document_name() {
            return (this.document && this.document.id > 0 ? this.document.title : 'New document');
        },
        document_id() {
            return (this.document && this.document.id > 0 ? this.document.id : '');
        },
    },
};

</script>
